<template>
  <container
    :isModal="params.isSearch"
    @close-modal="cancelSelection()"
    :loading="!stateReady"
  >
    <template v-slot:header>
      <h3>Contratos com Transportador</h3>
    </template>
    <template v-slot:body>
      <div>
        <form @submit.prevent="submitSearch">
          <entity-selector-cliente
            v-if="amIAllowed('view', 'root-cliente')"
            :cliente="cliente"
            :canEdit="!cliente"
            :idClienteFiltered="filter.idCliente"
            @confirm-selection="confirmSelectionCliente"
            :parentLoading="stateLoading || showEdit"
          />
          <fieldset :disabled="stateLoading || showEdit">
            <div class="p-formgrid p-grid">
              <div class="p-field p-fluid p-col-12 p-md-6">
                <entity-selector-transportador
                  :idTransportadorFiltered="filter.idTransportador ?? undefined"
                  :canEdit="true"
                  @confirm-selection="confirmSelectionTransportador"
                />
              </div>
              <div class="p-field p-fluid p-col-12 p-md-6">
                <entity-selector-orgao-aet
                  :idOrgao="filter.idOrgao ?? undefined"
                  @after-set-orgao="afterSetOrgao"
                />
              </div>
              <div class="p-col-12">
                <button-group>
                  <Button
                    type="submit"
                    class="p-button-rounded"
                    label="Buscar"
                  />
                  <Button
                    label="Limpar"
                    class="p-button-rounded p-button-text"
                    @click="clearFilter()"
                  />
                </button-group>
              </div>
            </div>
          </fieldset>
        </form>
      </div>
      <search-result
        :showSelector="params.isSearch"
        :results="results"
        :selected="params.selected"
        :multiple="params.isMultiple"
        :keyParam="'idContratoTransportador'"
        :loading="stateLoading"
        :showClient="true"
        @after-page-change="afterPageChange"
        @confirm-selection="confirmSelection"
      >
        <template v-slot:columns>
          <Column field="idTransportador" header="Transportador" />
          <Column field="idOrgao" header="Órgão" style="width: 12rem">
            <template #body="slotProps">
              <span v-text="getNomeOrgao(slotProps.data.idOrgao)" />
            </template>
          </Column>
          <Column field="tipoSolicitacao" header="Tipo Solicitação">
            <template #body="slotProps">
              <span
                v-text="
                  getDescricaoTipoSolicitacao(slotProps.data.tipoSolicitacao)
                "
              />
            </template>
          </Column>
          <Column field="precoFixo" header="Preço Fixo" style="width: 10rem">
            <template #body="slotProps">
              <span v-text="formatMoney(slotProps.data.precoFixo)" />
            </template>
          </Column>
          <Column field="dataInicio" header="Início" style="width: 9rem">
            <template #body="slotProps">
              <span v-text="formatDate(slotProps.data.dataInicio)" />
            </template>
          </Column>
          <Column field="dataFim" header="Fim" style="width: 9rem">
            <template #body="slotProps">
              <span v-text="formatDate(slotProps.data.dataFim)" />
            </template>
          </Column>
          <Column
            field="flAtivo"
            header="Ativo"
            style="width: 6rem; text-align: center"
          >
            <template #body="slotProps">
              <i
                v-if="slotProps.data.flAtivo"
                class="pi pi-check"
                style="color: #4CAF50"
              />
              <i v-else class="pi pi-times" style="color: #F44336" />
            </template>
          </Column>
          <Column :exportable="false" style="width: 10rem; text-align: right">
            <template #body="slotProps">
              <button-group>
                <Button
                  icon="pi pi-pencil"
                  class="p-button-rounded p-button-success"
                  :disabled="amIBlocked('view', slotProps.data.permissionInfo)"
                  @click="openEntity(slotProps.data.idContratoTransportador)"
                  @click.middle.prevent.stop="
                    openEntity(slotProps.data.idContratoTransportador, true)
                  "
                />
                <Button
                  icon="fas fa-trash-alt"
                  class="p-button-rounded p-button-danger"
                  v-if="
                    !params.isSearch &&
                      amIAllowed('shelve', slotProps.data.permissionInfo)
                  "
                  @click="shelveEntity(slotProps.data)"
                  :disabled="
                    amIBlocked('shelve', slotProps.data.permissionInfo)
                  "
                />
              </button-group>
            </template>
          </Column>
        </template>
        <template v-slot:actions>
          <Button
            label="Novo"
            :class="
              'p-button-rounded' + (params.isSearch ? ' p-button-text' : '')
            "
            v-if="amIAllowed('create', 'contrato-transportador')"
            @click="newEntity()"
          />
        </template>
      </search-result>
      <div class="register" v-if="showEdit">
        <cadastro-contrato-transportador
          :idContratoTransportador="idContratoTransportador"
          @after-register-screen-closed="afterRegisterScreenClosed"
          @after-save-new-entity="afterSaveNewEntity"
          :key="keyRegister"
          :parentLoading="stateLoading"
          :cliente="cliente"
        />
      </div>
    </template>
  </container>
</template>

<script lang="ts">
import CadastroContratoTransportador from "./CadastroContratoTransportador.vue";
import { PaginationResult, defaultResults } from "../../pagination";
import { useAuth, PermissionPayload } from "../../modules/auth";
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  PropType,
  Ref,
  watch
} from "vue";
import { RouteLocationNormalizedLoaded, useRouter } from "vue-router";
import {
  ClientePayload,
  useClienteService,
  useClienteLogadoService
} from "../../modules/business/cliente/clienteBusiness";
import {
  ContratoTransportadorPayload,
  ContratoTransportadorFilterPayload,
  useContratoTransportadorService
} from "../../modules/business/orcamento/contratoTransportadorBusiness";
import { useOrgaoAetService } from "../../modules/business/common/commonBusiness";
import { useTipoLicencaOrgaoService } from "../../modules/business/licenca/tipoLicencaOrgaoBusiness";
import { TransportadorPayload } from "../../modules/business/transportador/transportadorBusiness";
import { copyObject, clearObject } from "../../modules/utils";
import EntitySelectorCliente from "../cliente/EntitySelectorCliente.vue";
import EntitySelectorOrgaoAet from "../common/EntitySelectorOrgaoAet.vue";
import EntitySelectorTransportador from "../transportador/EntitySelectorTransportador.vue";
import { focusOnLastElement } from "../../modules/globalAppProperties";
import { useConfirm } from "../../modules/message";

interface ContratoTransportadorComponentParams {
  isSearch: boolean;
  selected: Array<ContratoTransportadorPayload>;
  isNew?: boolean;
  isMultiple?: boolean;
  idContratoTransportador?: number;
  preFilter?: ContratoTransportadorFilterPayload;
}

interface ContratoTransportadorCrudState {
  idContratoTransportador: number | null;
  showEdit: boolean;
  results: PaginationResult<ContratoTransportadorPayload>;
  filter: ContratoTransportadorFilterPayload;
  page: number;
  stateReady: boolean;
  params: ContratoTransportadorComponentParams;
  loadings: Array<Ref<boolean>>;
  stateId: string;
}

export default defineComponent({
  setup(props, { emit }) {
    const router = useRouter();

    const state = reactive<ContratoTransportadorCrudState>({
      idContratoTransportador: null,
      showEdit: false,
      stateReady: false,
      filter: useContratoTransportadorService().safeFilterContrato(),
      page: 0,
      results: defaultResults(),
      params: {
        isSearch: props.isSearch,
        isNew: props.isNew,
        idContratoTransportador: props.idContratoTransportador,
        selected: props.selected ?? [],
        isMultiple: props.isMultiple,
        preFilter: props.preFilter
      },
      loadings: [],
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2)
    });

    copyObject(clearObject(props.preFilter), state.filter);

    const stateLoading = computed(() => state.loadings.length !== 0);

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => useAuth().amIAllowed(authority, permissionInfo);

    const amIBlocked = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => useAuth().amIBlocked(authority, permissionInfo);

    const keyRegister = computed(() => {
      return state.showEdit
        ? state.idContratoTransportador
          ? state.idContratoTransportador
          : "new-" +
            Math.random()
              .toString(36)
              .substring(2)
        : null;
    });

    const executeSearch = () => {
      const {
        data,
        loading,
        searchContratos
      } = useContratoTransportadorService();
      state.loadings.push(loading);
      return searchContratos(state.filter, state.page)
        .then(() => {
          state.results = data.value;
          state.page = state.results.page.number;
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const submitSearch = () => {
      state.page = 0;
      state.results = defaultResults();
      return executeSearch();
    };

    const getParamsRouteEdit = (idContratoTransportador: number) => {
      return {
        name: "contrato-transportador-edit",
        params: { idContratoTransportador: idContratoTransportador + "" }
      };
    };

    const processViewRoute = (
      route: Partial<RouteLocationNormalizedLoaded>
    ) => {
      if (route.name == "contrato-transportador-new") {
        state.showEdit = true;
        state.idContratoTransportador = null;
      } else if (route.name == "contrato-transportador-edit") {
        if (route.params && route.params.idContratoTransportador) {
          state.idContratoTransportador = Array.isArray(
            route.params.idContratoTransportador
          )
            ? +route.params.idContratoTransportador[0]
            : +route.params.idContratoTransportador;
        } else {
          state.idContratoTransportador = null;
        }
        state.showEdit = true;
      } else {
        state.showEdit = false;
        state.idContratoTransportador = null;
        executeSearch();
      }
    };

    const confirmSelection = (
      selected: Array<ContratoTransportadorPayload>
    ) => {
      emit("confirm-selection", selected);
    };

    const cancelSelection = () => {
      emit("cancel-selection");
    };

    const shelveEntity = (entity: ContratoTransportadorPayload) => {
      useConfirm().require({
        message: "Deseja arquivar este contrato?",
        header: "Atenção",
        icon: "pi pi-info-circle",
        acceptClass: "p-button-danger",
        accept: () => {
          if (entity && entity.idContratoTransportador) {
            const {
              loading,
              shelveContrato
            } = useContratoTransportadorService();
            state.loadings.push(loading);
            shelveContrato(entity.idContratoTransportador)
              .then(() => {
                executeSearch();
                state.loadings.splice(state.loadings.indexOf(loading), 1);
              })
              .catch(() => {
                state.loadings.splice(state.loadings.indexOf(loading), 1);
              });
          }
        }
      });
    };

    const openEntity = (idContratoTransportador: number, newTab?: boolean) => {
      const routeParams = getParamsRouteEdit(idContratoTransportador);
      if (newTab) {
        window.open(router.resolve(routeParams).fullPath);
      } else if (state.params.isSearch) {
        processViewRoute(routeParams);
      } else {
        router.push(routeParams);
      }
    };

    const newEntity = () => {
      const routeParams = { name: "contrato-transportador-new" };
      if (state.params.isSearch) {
        processViewRoute(routeParams);
      } else {
        router.push(routeParams);
      }
    };

    const afterRegisterScreenClosed = () => {
      const routeParams = { name: "contrato-transportador" };
      if (state.params.isSearch) {
        processViewRoute(routeParams);
      } else {
        router.push(routeParams);
      }
    };

    const afterSaveNewEntity = (idContratoTransportador: number) => {
      openEntity(idContratoTransportador);
    };

    const afterPageChange = (page: number) => {
      state.page = page;
      executeSearch();
    };

    const clearFilter = () => {
      state.filter = useContratoTransportadorService().safeFilterContrato();
      state.results = defaultResults();
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : undefined;
    };

    const confirmSelectionTransportador = (t: TransportadorPayload | null) => {
      state.filter.idTransportador =
        t && t.idTransportador ? t.idTransportador : undefined;
    };

    const afterSetOrgao = (idOrgao: number | null) => {
      state.filter.idOrgao = idOrgao;
    };

    const getNomeOrgao = (idOrgao?: number | null) => {
      return useOrgaoAetService().getOrgaoAetStr(idOrgao ?? undefined);
    };

    const getDescricaoTipoSolicitacao = (
      idTipoLicencaOrgao?: number | null
    ) => {
      if (idTipoLicencaOrgao === null || idTipoLicencaOrgao === undefined)
        return "";
      return (
        useTipoLicencaOrgaoService().getTipoLicencaOrgao(idTipoLicencaOrgao)
          ?.descricao ?? ""
      );
    };

    const formatMoney = (valor?: number | null) => {
      if (valor === null || valor === undefined) return "";
      return valor.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
      });
    };

    const formatDate = (date?: Date | string | null) => {
      if (!date) return "";
      const d = typeof date === "string" ? new Date(date) : date;
      return d.toLocaleDateString("pt-BR");
    };

    const getNomeCliente = (e?: ClientePayload) => {
      return (amIAllowed("view", "root-cliente")
        ? useClienteService()
        : useClienteLogadoService()
      ).getNomeCliente(e);
    };

    const deveAbrirTelaCadastro =
      state.params.idContratoTransportador || state.params.isNew;
    if (deveAbrirTelaCadastro) {
      if (state.params.idContratoTransportador) {
        state.idContratoTransportador = state.params.idContratoTransportador;
      }
      state.showEdit = true;
    }

    onMounted(async () => {
      if (props.cliente && props.cliente.idCliente) {
        state.filter.idCliente = props.cliente.idCliente;
      }
      state.stateReady = true;
      if (!state.params.isSearch) {
        watch([router.currentRoute], () => {
          processViewRoute(router.currentRoute.value);
        });
      }
      if (!deveAbrirTelaCadastro) {
        submitSearch().then(() => focusOnLastElement());
      }
    });

    return {
      submitSearch,
      executeSearch,
      openEntity,
      newEntity,
      shelveEntity,
      confirmSelection,
      cancelSelection,
      afterRegisterScreenClosed,
      afterSaveNewEntity,
      afterPageChange,
      clearFilter,
      keyRegister,
      amIAllowed,
      amIBlocked,
      getNomeCliente,
      getNomeOrgao,
      getDescricaoTipoSolicitacao,
      formatMoney,
      formatDate,
      stateLoading,
      confirmSelectionCliente,
      confirmSelectionTransportador,
      afterSetOrgao,
      ...toRefs(state)
    };
  },
  components: {
    CadastroContratoTransportador,
    EntitySelectorCliente,
    EntitySelectorOrgaoAet,
    EntitySelectorTransportador
  },
  props: {
    isSearch: Boolean,
    isNew: Boolean,
    isMultiple: Boolean,
    idContratoTransportador: Number,
    preFilter: Object as PropType<ContratoTransportadorFilterPayload>,
    cliente: Object as PropType<ClientePayload>,
    selected: {
      type: Array as PropType<Array<ContratoTransportadorPayload>>
    }
  }
});
</script>

<style scoped lang="scss"></style>
